import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiCheckCircle, FiSmartphone } from "react-icons/fi";
import useAuth from "../../../hooks/useAuth";
import { repoUserService } from "../../../services/repoUser.service";
import { agencyRoleService } from "../../../services/agencyRole.service";
import BloodGroupPicker from "../../../components/userProfile/BloodGroupPicker";
import PincodeAutoFill from "../../../components/common/PincodeAutoFill";
import ManualDobFields from "../../../components/common/ManualDobFields";
import {
  partsToDobIso,
  validateDobParts,
} from "../../../utils/dateOfBirthUtils";
import "../../../styles/users.css";
import "../../../styles/profile.css";

const IDENTITY_MAX_BYTES = 5 * 1024 * 1024;
const IDENTITY_TYPES = new Set(["image/jpeg", "image/png", "application/pdf"]);
const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/i;

function isIdentityDocumentFile(file) {
  if (!file) return false;
  const mime = String(file.type || "").toLowerCase();
  const name = String(file.name || "").toLowerCase();
  const extOk = /\.(jpe?g|png|pdf)$/.test(name);
  if (extOk && (!mime || IDENTITY_TYPES.has(mime))) return true;
  return IDENTITY_TYPES.has(mime);
}

function isIdentityPdf(file) {
  if (!file) return false;
  const mime = String(file.type || "").toLowerCase();
  const name = String(file.name || "").toLowerCase();
  return mime === "application/pdf" || name.endsWith(".pdf");
}

export default function CreateUser() {
  const navigate = useNavigate();
  const { auth } = useAuth();

  const [form, setForm] = useState({
    name: "",
    fatherName: "",
    email: "",
    dateOfBirth: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    panNumber: "",
    aadhaarNumber: "",
    bloodGroup: "",
    password: "",
    role: "OFFICE_STAFF",
    agencyRoleId: "",
  });

  const [dobParts, setDobParts] = useState({
    day: "",
    month: "",
    year: "",
  });

  const [otp, setOtp] = useState("");
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpMessage, setOtpMessage] = useState("");
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [agencyRoles, setAgencyRoles] = useState([]);
  const [panDocumentFile, setPanDocumentFile] = useState(null);
  const [aadhaarDocumentFile, setAadhaarDocumentFile] = useState(null);
  const [panDocumentPreview, setPanDocumentPreview] = useState("");
  const [aadhaarDocumentPreview, setAadhaarDocumentPreview] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    let next = value;

    if (name === "pincode") {
      next = value.replace(/\D/g, "").slice(0, 6);
    } else if (name === "aadhaarNumber") {
      next = value.replace(/\D/g, "").slice(0, 12);
    } else if (name === "panNumber") {
      next = value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase().slice(0, 10);
    }

    setForm((prev) => ({
      ...prev,
      [name]: next,
    }));

    if (name === "phone") {
      setPhoneVerified(false);
      setOtpSent(false);
      setOtp("");
      setOtpMessage("");
    }
  };

  useEffect(() => {
    if (!auth?.token) return undefined;
    let cancelled = false;
    agencyRoleService
      .getRoles(auth.token)
      .then((res) => {
        if (cancelled) return;
        setAgencyRoles(Array.isArray(res?.roles) ? res.roles : []);
      })
      .catch(() => {
        if (!cancelled) setAgencyRoles([]);
      });
    return () => {
      cancelled = true;
    };
  }, [auth?.token]);

  const handleIdentityDocumentPick = (kind, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!isIdentityDocumentFile(file)) {
      setError("Upload a JPG, JPEG, PNG, or PDF file for PAN and Aadhaar.");
      return;
    }
    if (file.size > IDENTITY_MAX_BYTES) {
      setError("Each document must be 5 MB or smaller.");
      return;
    }
    const preview = isIdentityPdf(file) ? "" : URL.createObjectURL(file);
    if (kind === "pan") {
      setPanDocumentFile(file);
      setPanDocumentPreview(preview);
    } else {
      setAadhaarDocumentFile(file);
      setAadhaarDocumentPreview(preview);
    }
    setError("");
  };

  const handleDobChange = (parts) => {
    setDobParts(parts);

    setForm((prev) => ({
      ...prev,
      dateOfBirth: partsToDobIso(parts),
    }));
  };

  const handleSendOtp = async () => {
    if (!form.phone.trim()) {
      setError("Enter mobile number first.");
      return;
    }

    setSendingOtp(true);
    setError("");
    setOtpMessage("");

    try {
      const res = await repoUserService.sendPhoneOtp(
        form.phone.trim(),
        auth.token
      );

      setOtpSent(true);
      setPhoneVerified(false);
      setOtp("");
      setOtpMessage(
        res?.message || "OTP sent. Check your SMS."
      );
      setError("");
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "Failed to send OTP."
      );
    } finally {
      setSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp.trim()) {
      setError("Enter the OTP you received.");
      return;
    }

    setVerifyingOtp(true);
    setError("");

    try {
      const res = await repoUserService.verifyPhoneOtp(
        form.phone.trim(),
        otp.trim(),
        auth.token
      );

      setPhoneVerified(true);

      setOtpMessage(
        res?.message || "Mobile number verified."
      );
    } catch (err) {
      setPhoneVerified(false);

      setError(
        err?.response?.data?.message ||
          "OTP verification failed."
      );
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.bloodGroup) {
      setError("Please select a blood group.");
      return;
    }

    if (!form.state.trim()) {
      setError("Please select a state.");
      return;
    }

    const dobError = validateDobParts(dobParts);

    if (dobError) {
      setError(dobError);
      return;
    }

    if (!form.agencyRoleId) {
      setError("Please select a role.");
      return;
    }

    if (!PAN_REGEX.test(form.panNumber.trim())) {
      setError("PAN number must be 10 characters (AAAAA9999A).");
      return;
    }
    if (form.aadhaarNumber.replace(/\D/g, "").length !== 12) {
      setError("Aadhaar number must be 12 digits.");
      return;
    }
    if (!panDocumentFile) {
      setError("PAN card image or PDF is required.");
      return;
    }
    if (!aadhaarDocumentFile) {
      setError("Aadhaar card image or PDF is required.");
      return;
    }

    if (!phoneVerified) {
      setError(
        "Please verify the mobile number with OTP before creating the user."
      );
      return;
    }

    setLoading(true);
    setError("");

    try {
      const payload = new FormData();
      payload.append("name", form.name);
      payload.append("fatherName", form.fatherName);
      payload.append("email", form.email);
      payload.append("dateOfBirth", partsToDobIso(dobParts));
      payload.append("phone", form.phone);
      payload.append("address", form.address);
      payload.append("city", form.city);
      payload.append("state", form.state);
      payload.append("pincode", form.pincode);
      payload.append("bloodGroup", form.bloodGroup);
      payload.append("password", form.password);
      payload.append("role", form.role);
      payload.append("agencyRoleId", form.agencyRoleId);
      payload.append("panNumber", form.panNumber.trim().toUpperCase());
      payload.append("aadhaarNumber", form.aadhaarNumber.replace(/\D/g, ""));
      payload.append("panDocument", panDocumentFile);
      payload.append("aadhaarDocument", aadhaarDocumentFile);

      await repoUserService.createUser(payload, auth.token);

      alert("Repo user created successfully");

      navigate("/users");
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "Failed to create user"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page create-company-page repo-form-page profile-page">
      <div className="create-company-header">
        <div>
          <h2>Create Repo User</h2>

          <p className="muted">
            Add team leader, head office staff, or office staff.
            Enter pincode to auto-fill city and state.
          </p>
        </div>

        <button
          type="button"
          className="secondary-page-btn"
          onClick={() => navigate("/users")}
        >
          Back to Users
        </button>
      </div>

      <form
        className="create-company-form"
        onSubmit={handleSubmit}
      >
        <div className="create-company-card">
          <section
            className="create-company-section"
            aria-labelledby="create-user-personal"
          >
            <header
              className="create-company-section__head"
              id="create-user-personal"
            >
              <h3>Personal details</h3>
            </header>

            <div className="form-grid two-column">
              <div className="form-group">
                <label htmlFor="name">
                  Full name *
                </label>

                <input
                  id="name"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Enter full name"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="fatherName">
                  Father's name *
                </label>

                <input
                  id="fatherName"
                  name="fatherName"
                  value={form.fatherName}
                  onChange={handleChange}
                  placeholder="Father's name"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">
                  Email *
                </label>

                <input
                  id="email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="user@email.com"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="role">
                  Role *
                </label>

                <select
                  id="role"
                  name="agencyRoleId"
                  value={form.agencyRoleId}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select a role</option>
                  {agencyRoles.map((role) => (
                    <option
                      key={role.id || role._id}
                      value={role.id || role._id}
                    >
                      {role.name}
                    </option>
                  ))}
                </select>
              </div>

              <>
                  <div className="form-group">
                    <label htmlFor="panNumber">PAN Number *</label>
                    <input
                      id="panNumber"
                      name="panNumber"
                      value={form.panNumber}
                      onChange={handleChange}
                      placeholder="10-character PAN"
                      maxLength={10}
                      required
                      style={{ textTransform: "uppercase" }}
                    />
                    <label htmlFor="create-user-pan-document">
                      Upload PAN Card *
                    </label>
                    <input
                      id="create-user-pan-document"
                      type="file"
                      accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                      onChange={(e) => handleIdentityDocumentPick("pan", e)}
                      required={!panDocumentFile}
                    />
                    {panDocumentFile ? (
                      <div className="tax-doc-upload">
                        {panDocumentPreview ? (
                          <img
                            className="tax-doc-upload__preview"
                            src={panDocumentPreview}
                            alt=""
                          />
                        ) : null}
                        <span className="tax-doc-upload__name">
                          {panDocumentFile.name}
                        </span>
                      </div>
                    ) : (
                      <p className="field-hint muted">JPG, JPEG, PNG, or PDF</p>
                    )}
                  </div>
                  <div className="form-group">
                    <label htmlFor="aadhaarNumber">Aadhaar Number *</label>
                    <input
                      id="aadhaarNumber"
                      name="aadhaarNumber"
                      value={form.aadhaarNumber}
                      onChange={handleChange}
                      placeholder="12-digit Aadhaar number"
                      inputMode="numeric"
                      maxLength={12}
                      required
                    />
                    <label htmlFor="create-user-aadhaar-document">
                      Upload Aadhaar Card *
                    </label>
                    <input
                      id="create-user-aadhaar-document"
                      type="file"
                      accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                      onChange={(e) =>
                        handleIdentityDocumentPick("aadhaar", e)
                      }
                      required={!aadhaarDocumentFile}
                    />
                    {aadhaarDocumentFile ? (
                      <div className="tax-doc-upload">
                        {aadhaarDocumentPreview ? (
                          <img
                            className="tax-doc-upload__preview"
                            src={aadhaarDocumentPreview}
                            alt=""
                          />
                        ) : null}
                        <span className="tax-doc-upload__name">
                          {aadhaarDocumentFile.name}
                        </span>
                      </div>
                    ) : (
                      <p className="field-hint muted">JPG, JPEG, PNG, or PDF</p>
                    )}
                  </div>
              </>

              <div className="form-group full-width">
                <label>Blood group *</label>

                <BloodGroupPicker
                  value={form.bloodGroup}
                  onChange={(bloodGroup) =>
                    setForm((prev) => ({
                      ...prev,
                      bloodGroup,
                    }))
                  }
                  required
                />
              </div>

              <div className="form-grid two-column profile-contact-grid full-width">
                <div className="form-group form-group--dob">
                  <label htmlFor="create-user-dob-day">
                    Date of birth *
                  </label>

                  <ManualDobFields
                    value={form.dateOfBirth}
                    onChange={handleDobChange}
                    idPrefix="create-user-dob"
                  />

                  <p className="field-hint muted">
                    Format: DD / MM / YYYY
                  </p>
                </div>

                <div className="form-group">
                  <label htmlFor="password">
                    Password *
                  </label>

                  <input
                    id="password"
                    name="password"
                    type="password"
                    value={form.password}
                    onChange={handleChange}
                    placeholder="Minimum 6 characters"
                    minLength={6}
                    required
                  />
                </div>
              </div>
            </div>
          </section>

          <section
            className="create-company-section"
            aria-labelledby="create-user-address"
          >
            <header
              className="create-company-section__head"
              id="create-user-address"
            >
              <h3>Address</h3>
            </header>

            <div className="form-grid two-column">
              <div className="form-group full-width">
                <label htmlFor="address">
                  Address *
                </label>

                <input
                  id="address"
                  name="address"
                  value={form.address}
                  onChange={handleChange}
                  placeholder="House no., street, area"
                  required
                />
              </div>

              <PincodeAutoFill
                pincode={form.pincode}
                onPincodeChange={(value) =>
                  setForm((prev) => ({ ...prev, pincode: value }))
                }
                city={form.city}
                onCityChange={(value) =>
                  setForm((prev) => ({ ...prev, city: value }))
                }
                state={form.state}
                onStateChange={(value) =>
                  setForm((prev) => ({ ...prev, state: value }))
                }
                cityLabel="City"
              />
            </div>
          </section>

          <section
            className="create-company-section create-company-section--last phone-otp-section"
            aria-labelledby="create-user-mobile"
          >
            <header
              className="create-company-section__head phone-otp-card-head"
              id="create-user-mobile"
            >
              <FiSmartphone
                size={22}
                aria-hidden
              />

              <div>
                <h3>Mobile verification</h3>

                <p className="muted profile-section-desc">
                  Send OTP, then enter the 6-digit code received
                  by SMS.
                </p>
              </div>
            </header>

            <div className="form-grid two-column">
              <div className="form-group">
                <label htmlFor="phone">
                  Mobile number
                </label>

                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="10-digit mobile number"
                  required
                />
              </div>

              <div className="form-group phone-otp-actions">
                <label>&nbsp;</label>

                <button
                  type="button"
                  className="company-btn company-btn--reset"
                  onClick={handleSendOtp}
                  disabled={
                    sendingOtp ||
                    !form.phone.trim()
                  }
                >
                  {sendingOtp
                    ? "Sending OTP..."
                    : "Send OTP"}
                </button>
              </div>
            </div>

            {otpSent && !phoneVerified && (
              <div className="phone-otp-verify-row">
                <div className="form-group">
                  <label htmlFor="otp">
                    Enter OTP
                  </label>

                  <input
                    id="otp"
                    name="otp"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={otp}
                    onChange={(e) =>
                      setOtp(
                        e.target.value.replace(
                          /\D/g,
                          ""
                        )
                      )
                    }
                    placeholder="6-digit OTP"
                  />
                </div>

                <button
                  type="button"
                  className="company-btn company-btn--view"
                  onClick={handleVerifyOtp}
                  disabled={
                    verifyingOtp ||
                    otp.length < 6
                  }
                >
                  {verifyingOtp
                    ? "Verifying..."
                    : "Verify OTP"}
                </button>
              </div>
            )}

            {otpMessage && (
              <p
                className={`phone-otp-message${
                  phoneVerified
                    ? " success"
                    : ""
                }`}
              >
                {otpMessage}
              </p>
            )}

            {phoneVerified && (
              <p className="phone-otp-verified">
                <FiCheckCircle aria-hidden />

                Mobile number verified — you can create
                the user now.
              </p>
            )}
          </section>
        </div>

        {error && (
          <p className="error-text create-company-error">
            {error}
          </p>
        )}

        <div className="create-company-actions create-company-actions--sticky">
          <button
            type="submit"
            className="primary-page-btn"
            disabled={
              loading || !phoneVerified
            }
          >
            {loading
              ? "Creating..."
              : "Create User"}
          </button>
        </div>
      </form>
    </div>
  );
}